export interface ActiveSolutionItem {
  id?: string;
  name?: string;
}

interface SelectedSolutionLike {
  uniqueId?: string;
  displayName?: string;
}

/**
 * Decide the next `activeSolution` for the solutions-dropdown breadcrumb from the
 * XState `selectedSolution`. Returns `{ shouldUpdate: false }` when the current
 * value already matches (avoids a redundant setState), otherwise
 * `{ shouldUpdate: true, next }` with the value to apply (`null` on the home
 * page, where the breadcrumb shows "All Solutions").
 *
 * The "unchanged" guard compares BOTH the stable `uniqueId` AND the
 * `displayName` — not the id alone. A RENAME of the current solution keeps the
 * same `uniqueId` and only changes `displayName`, so an id-only guard left the
 * breadcrumb showing the stale name until a page reload (PSTUDIO-3596). Keeping
 * this pure lets it be unit-tested in the node env (pro-studio has no RTL).
 */
export function computeActiveSolution({
  selectedSolution,
  activeSolution,
  isHomePage,
}: {
  selectedSolution?: SelectedSolutionLike | null;
  activeSolution?: ActiveSolutionItem | null;
  isHomePage: boolean;
}): { shouldUpdate: boolean; next?: ActiveSolutionItem | null } {
  if (!selectedSolution) return { shouldUpdate: false };

  if (
    selectedSolution.uniqueId === activeSolution?.id &&
    selectedSolution.displayName === activeSolution?.name
  ) {
    return { shouldUpdate: false };
  }

  if (isHomePage) return { shouldUpdate: true, next: null };

  return {
    shouldUpdate: true,
    next: {
      id: selectedSolution.uniqueId,
      name: selectedSolution.displayName,
    },
  };
}

interface ScrollTopParams {
  /** Item top edge in viewport coordinates (getBoundingClientRect().top). */
  itemTop: number;
  /** Item bottom edge in viewport coordinates. */
  itemBottom: number;
  /** Container top edge in viewport coordinates. */
  containerTop: number;
  /** Container bottom edge in viewport coordinates. */
  containerBottom: number;
  /** Current container.scrollTop. */
  scrollTop: number;
}

/**
 * Pure scroll math for bringing a list item into view within its own
 * overflow container — the container is the only thing that moves, never the
 * page or the popover. Mirrors the design-system `scrollIntoView` vertical
 * behaviour but stays framework-free so it can be unit-tested in the node env
 * (pro-studio has no jsdom/RTL).
 *
 * Returns the new `scrollTop`:
 * - item above the viewport  → align its top edge to the container top
 * - item below the viewport  → align its bottom edge to the container bottom
 * - item already fully visible → unchanged
 */
export function computeScrollTopForItem({
  itemTop,
  itemBottom,
  containerTop,
  containerBottom,
  scrollTop,
}: ScrollTopParams): number {
  if (itemTop < containerTop) {
    return scrollTop + (itemTop - containerTop);
  }
  if (itemBottom > containerBottom) {
    return scrollTop + (itemBottom - containerBottom);
  }
  return scrollTop;
}
